import React from 'react';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import SimpleLogin from '../components/auth/SimpleLogin';
import ResetPassword from '../components/auth/ResetPassword';
import AttendanceCheckIn from '../components/attendance/AttendanceCheckIn';
import ExtensionActionLanding from '../components/extensions/ExtensionActionLanding';
import PublicSurveyPlayer from '../pages/public/surveys/PublicSurveyPlayer';
import SurveyClassLink from '../pages/public/surveys/SurveyClassLink';
import PurchaseConfirmation from '../components/payment/PurchaseConfirmation';
import DonationForm from '../components/payment/DonationForm';
import DonationConfirmation from '../components/payment/DonationConfirmation';
import EnrollmentFlow from '../components/payment/EnrollmentFlow';
import CourseCatalog from '../components/payment/CourseCatalog';
import GuestPayment from '../components/payment/GuestPayment';
import GuestCheckoutComplete from '../components/payment/GuestCheckoutComplete';
import logo from '../assets/logo.png';
import { AuthContainer, AuthCard, Logo } from './App.styles';

// Any logged-out-only path that isn't one of the explicit public routes
// above (e.g. a direct link to /instructor/surveys/1/preview, /admin/...,
// /student/...) lands here. Without preserving where they were headed,
// LoginRedirect.jsx falls back to its own default (/dashboard) once they
// log in — so this carries the attempted path+search through as the same
// ?redirect= param the explicit routes above already use (see
// /attendance/checkin's Navigate a few lines up).
function RedirectToLoginWithReturn() {
  const location = useLocation();
  const attemptedPath = location.pathname + location.search;
  return <Navigate to={`/login?redirect=${encodeURIComponent(attemptedPath)}`} replace />;
}

// A logged-out visitor opening a direct payment URL is sent to the email-first
// enrollment step (which signs existing students in inline) instead of the
// generic login page. The authenticated /enroll/flow and /enroll/products
// routes bounce signed-in users straight back to payment.
function RedirectToEnrollmentFlow({ kind }) {
  const { courseId, courseIds, productIds } = useParams();
  if (kind === 'products') return <Navigate to={`/enroll/products/${productIds}`} replace />;
  return <Navigate to={`/enroll/flow/${courseIds || courseId}`} replace />;
}

function PublicRoutes() {
  return (
    <Routes>
        {/* Public routes that don't need auth card styling */}
        <Route path="/purchase/confirmation" element={<PurchaseConfirmation />} />
        <Route path="/confirmation" element={<PurchaseConfirmation />} />
        <Route path="/catalog" element={<CourseCatalog />} />
        <Route path="/enroll/flow/:courseIds" element={<EnrollmentFlow />} />
        <Route path="/enroll/products/:productIds" element={<EnrollmentFlow />} />
        <Route path="/enroll/guest/complete" element={<GuestCheckoutComplete />} />
        <Route path="/enroll/guest/products/:productIds" element={<GuestPayment />} />
        <Route path="/enroll/guest/:courseIds" element={<GuestPayment />} />
        <Route path="/enroll/multiple/:courseIds" element={<RedirectToEnrollmentFlow kind="courses" />} />
        <Route path="/purchase/products/:productIds" element={<RedirectToEnrollmentFlow kind="products" />} />
        <Route path="/enroll/:courseId" element={<RedirectToEnrollmentFlow kind="courses" />} />
        <Route path="/donation" element={<DonationForm />} />
        <Route path="/donation/success" element={<DonationConfirmation />} />

        {/* Auth routes that need the auth card styling */}
        <Route path="/login" element={
          <AuthContainer>
            <AuthCard
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Logo>
                <img src={logo} alt="Faith Insight Logo" />
                <h1>Faith Insight</h1>
                <p>Learning Portal</p>
              </Logo>
              <SimpleLogin />
            </AuthCard>
          </AuthContainer>
        } />
        {/* The old sign-up-first flow is gone: new students pick courses/products in the public
            catalog and their account is created when they pay. Old links land there. */}
        <Route path="/signup" element={<Navigate to="/catalog" replace />} />
        <Route path="/reset-password" element={
          <AuthContainer>
            <AuthCard
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Logo>
                <img src={logo} alt="Faith Insight Logo" />
                <h1>Faith Insight</h1>
                <p>Learning Portal</p>
              </Logo>
              <ResetPassword />
            </AuthCard>
          </AuthContainer>
        } />
        <Route path="/attendance/checkin" element={<Navigate to="/login?redirect=%2Fattendance%2Fcheckin" replace />} />
        <Route path="/attendance/checkin/:sessionId" element={<AttendanceCheckIn />} />
        <Route path="/extensions/action" element={<ExtensionActionLanding />} />
        <Route path="/survey/respond/:token" element={<PublicSurveyPlayer />} />
        <Route path="/survey/class/:token" element={<SurveyClassLink />} />
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="*" element={<RedirectToLoginWithReturn />} />
      </Routes>
  );
}

export default PublicRoutes;
